<template>
  <div>

    <modal
      :title="`${lpContext?.name}: ${$t('actions.bulk_edit_cost')}`"
      :submit="submit"
      :submitting="submitting"
      ref="uriCostModal"
    >

      <v-row>

        <v-col>
          <date-range-field
            :label="$t('titles.date_range')"
            v-model="formState.dateRange"
            :rules="formRules.dateRange"
            :min="today"
          />
        </v-col>

        <v-col>

          <weekday-field
            :label="$t('titles.weekdays')"
            v-model="formState.weekdays"
            multiple
            :rules="formRules.weekdays"
          />

        </v-col>

      </v-row>

      <uri-daily-rates
        v-model="formState.channelCosts"
        :context="lpContext"
        :collapsible="false"
        :collapsed="false"
      />

    </modal>

  </div>
</template>

<script setup lang="ts">

import { computed, onBeforeMount, reactive, ref, useTemplateRef } from 'vue'

// components
import DateRangeField from '../../../forms/DateRangeField.vue'
import WeekdayField from '../../../forms/WeekdayField.vue'
import UriDailyRates from './UriDailyRates.vue'
import Modal from '../../../Modal.vue'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import { required } from '../../../../validation'
import { Temporal } from '@js-temporal/polyfill'
import NotificationService from '../../../../services/NotificationService'
import RatesApi, { type SyncSpotRatesRequest } from '../../../../services/api-clients/RatesApi'
import { range } from 'underscore'
import type { UriContext, UriLpContext } from './types'
import type { ChannelDefaultRateFieldState, DateRange } from '../../../../types/misc'

interface Props {
  locationProductId: number
  context: UriContext
}

const props = defineProps<Props>()

const today = Temporal.Now.plainDateISO().toString()

const lpContext = computed<UriLpContext>((): UriLpContext => {
  return props.context.lps.find(lpContext => lpContext.lpId === props.locationProductId)!
})

/**
 * Form state and rules
 */

interface FormState {
  dateRange: DateRange | null
  weekdays: number[]
  channelCosts: ChannelDefaultRateFieldState[],
}

const formState = reactive<FormState>({
  dateRange: null,
  weekdays: [7, 1, 2, 3, 4, 5, 6],
  channelCosts: [],
})

const formRules = {
  dateRange: [required()],
  weekdays: [required()],
}

/**
 * Initialization
 */

onBeforeMount(() => {

  lpContext.value.defaultRate.channelDefaultRates.forEach((cdrContext, channelFacilityId) => {
    const channelFacility = lpContext.value.channelFacilities.get(channelFacilityId)
    // const channelCost = cdrContext.channelCost !== defaultCost ? cdrContext.channelCost : null
    formState.channelCosts.push({
      id: cdrContext.cdrId,
      channelCost: cdrContext.channelCost, // channelCost,
      originalChannelCost: cdrContext.channelCost, // channelCost,
      channelFacility: channelFacility!,
    })
  })

})

/**
 * Submission
 */

const uriCostModal = useTemplateRef<InstanceType<typeof Modal>>('uriCostModal')
const submitting = ref<boolean>(false)

async function submit() {

  await uriCostModal.value!.modalForm!.validate()

  if (!uriCostModal.value!.modalForm!.isValid) {
    return
  }

  const request: SyncSpotRatesRequest = {spotRates: []}

  const numDays = formState.dateRange!.start.until(formState.dateRange!.end).days + 1
  const dates = range(0, numDays).map(i => formState.dateRange!.start.add({days: i})).filter(d => formState.weekdays.includes(d.dayOfWeek))

  formState.channelCosts.forEach(formCdr => {
    if (formCdr.channelCost === formCdr.originalChannelCost) {
      return
    }
    dates.forEach(date => {
      request.spotRates.push({
        date,
        locationProductId: props.locationProductId,
        defaultRateId: lpContext.value.defaultRate.drId,
        channelDefaultRateId: formCdr.id!,
        channelCost: formCdr.channelCost!,
      })
    })
  })

  if (!request.spotRates.length) {
    NotificationService.info('None of the selected weekdays are in this date range, or no costs have been changed')
    return
  }

  submitting.value = true

  RatesApi.syncSpotRates(request)
    .then(() => {
      NotificationService.success('Rates updated')
      closeDialog(true)
    })
    .catch(e => {
      console.error(e)
      NotificationService.error('Couldn\'t save rates!')
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
